import React, { useRef } from 'react';
import {
	Box,
	Checkbox,
	Chip,
	Grid,
	IconButton,
	Tooltip,
	Typography,
} from '@mui/material';
import {
	Delete,
	Edit,
	LinkOffRounded,
	WarningAmberRounded,
} from '@mui/icons-material';
import { PercentSlider } from '@Components/PercentSlider';
import { useStyles } from './Component.styles';
import { MenuListButton } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';
import { TFunc } from '@Translations/types';
import { ComponentCoveragePill } from './ComponentCoveragePill';

export interface ComponentProps {
	title: string;
	subTitle?: string;
	value: number;
	disableSlider?: boolean;
	invalid?: boolean;
	onSliderValueChanged?: (value: number) => void;
	onSelectChecked?: (selected: boolean) => void;
	needMenu?: boolean;
	onEdit?: (title: string) => void;
	onDelete?: (title: string) => void;
	checkBox?: boolean;
	enabledChecked?: boolean;
	// True when this component's saved source isn't in the tenant's data
	// (see selectMissingSourceKind) — shows an amber "Component to add" chip
	// so the user knows it still needs configuring.
	missing?: boolean;
	/** What kind of source is missing ("attribute", "task"...), for the
	 *  "Not in your data" tooltip. */
	missingKind?: string;
	/** Data points in this component still without a score. 0/undefined hides
	 *  the amber "N to score" pill. */
	needsScoreCount?: number;
	categoryName?: string;
	/** Real data coverage for this component. Pass only when the pill should
	 *  show (see hasCoverageToShow) — omit for ineligible types, uncomputed
	 *  coverage, or no assets in scope. */
	coverage?: { pct: number; assets: number };
	/** True when a heavy weight rests on thin data (isHighWeightLowCoverage). */
	thinDataWarning?: boolean;
	/** Opt into the redesigned risk-templates slider look. Gated by the
	 *  caller on useIsRiskTemplatesEnabled — defaults to false so existing
	 *  callers (e.g. CoF Setup, flag off) render exactly as before. */
	redesigned?: boolean;
	/** 'primary' = the category's own accent colour; 'neutral' = grey for a
	 *  component's weight within its category. Only used when `redesigned`. */
	tone?: 'primary' | 'neutral';
	/** Explicit accent colour — overrides `tone`. Only used when `redesigned`. */
	accent?: string;
}

interface ComponentTitleProps {
	title: string;
	hasSubTitle: boolean;
	editable: boolean;
	onEdit?: (title: string) => void;
}

// The component name — click-to-edit when `editable` (redesigned + onEdit
// provided). Extracted into its own function so its several
// `editable`-gated attributes don't add to Component's own cognitive
// complexity. `hasSubTitle` restores the classic layout's gutterBottom,
// tuned against the subtitle's own negative marginTop below.
const ComponentTitle = ({
	title,
	hasSubTitle,
	editable,
	onEdit,
}: ComponentTitleProps) => {
	const { t } = useGlobalization();

	const handleClick = (e: React.MouseEvent) => {
		// Stop this from also bubbling to the card's own onClick
		// (handleCardClick), which would call onEdit a second time for the
		// same click.
		e.stopPropagation();
		onEdit?.(title);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key !== 'Enter' && e.key !== ' ') return;
		e.preventDefault();
		onEdit?.(title);
	};

	const editableProps = editable
		? {
				onClick: handleClick,
				onKeyDown: handleKeyDown,
				role: 'button' as const,
				tabIndex: 0,
				'aria-label': t('Edit {{name}}', { name: title }),
				sx: { '&:hover': { textDecoration: 'underline' } },
		  }
		: {};

	return (
		<Typography
			gutterBottom={hasSubTitle}
			{...editableProps}
			style={{
				marginLeft: '0px',
				fontWeight: 600,
				fontSize: '14px',
				lineHeight: '20px',
				cursor: editable ? 'pointer' : undefined,
			}}>
			{title}
		</Typography>
	);
};

// Translated name for the kind of source a component is missing (see
// MissingSourceKind). Literal t() calls so the extractor picks them up;
// unknown kinds fall back to a generic label rather than leaking the raw value.
const missingKindLabel = (t: TFunc, kind?: string): string => {
	switch (kind) {
		case 'attribute':
			return t('attribute');
		case 'observation':
			return t('observation');
		case 'task':
			return t('task');
		case 'spatialProximity':
		case 'spatialAttribute':
			return t('spatial layer');
		default:
			return t('data source');
	}
};

// Amber warning pill shared by "Component to add" and "N to score".
const amberChipSx = {
	backgroundColor: 'rgba(250, 162, 27, 0.14)',
	color: '#8a5a12',
	fontWeight: 700,
	fontSize: '11px',
	height: '20px',
	'& .MuiChip-icon': { fontSize: 14, color: '#e0872f' },
};

interface ComponentLabelAreaProps {
	isCard: boolean;
	title: string;
	subTitle?: string;
	missing: boolean;
	missingKind?: string;
	needsScoreCount: number;
	coverage?: { pct: number; assets: number };
	thinDataWarning: boolean;
	editable: boolean;
	onEdit?: (title: string) => void;
}

// Name + subtitle + "Component to add" chip — the left-hand column in both
// the classic row and the redesigned card.
const ComponentLabelArea = ({
	isCard,
	title,
	subTitle,
	missing,
	missingKind,
	needsScoreCount,
	coverage,
	thinDataWarning,
	editable,
	onEdit,
}: ComponentLabelAreaProps) => {
	const { t } = useGlobalization();

	return (
		<Grid item xs={isCard ? 5 : 2} container>
			<Grid item xs={12}>
				<ComponentTitle
					title={title}
					hasSubTitle={Boolean(subTitle)}
					editable={editable}
					onEdit={onEdit}
				/>
			</Grid>
			{subTitle && (
				<Grid item xs={12}>
					<Typography
						gutterBottom
						style={{
							fontWeight: 400,
							fontSize: '10px',
							lineHeight: '12px',
							marginTop: '-7px',
						}}>
						{subTitle}
					</Typography>
				</Grid>
			)}
			{coverage && (
				<Grid item xs={12} sx={{ mb: 0.5 }}>
					<ComponentCoveragePill
						pct={coverage.pct}
						assets={coverage.assets}
					/>
				</Grid>
			)}
			{thinDataWarning && (
				<Grid
					item
					xs={12}
					data-cy="component-thin-data-warning"
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						gap: 0.5,
					}}>
					<WarningAmberRounded
						sx={{ fontSize: 14, color: '#e0872f', mt: '1px' }}
					/>
					<Typography sx={{ fontSize: 11.5, color: '#8a6d3b' }}>
						{t(
							'Heavy weight on thin data — most assets will use the score you assign to "--".',
						)}
					</Typography>
				</Grid>
			)}
			{missing && (
				<>
					<Grid item xs={12}>
						<Tooltip
							arrow
							title={t(
								'This component is not in your data yet, so it will not score any assets until you connect the {{kind}}.',
								{ kind: missingKindLabel(t, missingKind) },
							)}>
							<Chip
								size="small"
								icon={<LinkOffRounded />}
								label={t('Not in your data')}
								data-cy="component-not-in-data-chip"
								sx={{
									backgroundColor: '#eef1f4',
									color: '#5b6472',
									fontWeight: 600,
									fontSize: '11px',
									height: '20px',
								}}
							/>
						</Tooltip>
					</Grid>
					<Grid item xs={12} sx={{ mt: 0.5 }}>
						<Chip
							size="small"
							icon={<WarningAmberRounded />}
							label={t('Component to add')}
							data-cy="component-missing-chip"
							sx={amberChipSx}
						/>
					</Grid>
				</>
			)}
			{!missing && needsScoreCount > 0 && (
				<Grid item xs={12} sx={{ mt: 0.5 }}>
					<Tooltip
						arrow
						title={t(
							'{{count}} of your values for this component have no score yet. Open it to give each one a score.',
							{ count: needsScoreCount },
						)}>
						<Chip
							size="small"
							icon={<WarningAmberRounded />}
							label={t('{{count}} to score', {
								count: needsScoreCount,
							})}
							data-cy="component-needs-score-chip"
							sx={amberChipSx}
						/>
					</Tooltip>
				</Grid>
			)}
		</Grid>
	);
};

interface ComponentActionsProps {
	needMenu: boolean;
	redesigned: boolean;
	title: string;
	onEdit?: (title: string) => void;
	onDelete?: (title: string) => void;
}

// The row/card's trailing action(s): a delete icon in the redesigned
// layout, or the classic three-dot Edit/Delete menu.
const ComponentActions = ({
	needMenu,
	redesigned,
	title,
	onEdit,
	onDelete,
}: ComponentActionsProps) => {
	const { t } = useGlobalization();

	if (!needMenu) return null;

	if (redesigned) {
		return (
			<Tooltip arrow title={t('Delete {{name}}', { name: title })}>
				<IconButton
					size="small"
					aria-label={t('Delete {{name}}', { name: title })}
					data-cy="actions"
					onClick={e => {
						e.stopPropagation();
						onDelete?.(title);
					}}>
					<Delete fontSize="small" />
				</IconButton>
			</Tooltip>
		);
	}

	return (
		<MenuListButton
			menuItems={[
				{
					text: t('Edit'),
					icon: <Edit />,
					onClick: () => onEdit?.(title),
				},
				{
					text: t('Delete'),
					icon: <Delete />,
					onClick: () => onDelete?.(title),
				},
			]}
			buttonCy="actions"
		/>
	);
};

interface ComponentControlProps {
	checkBox: boolean;
	disableSlider: boolean;
	enabledChecked?: boolean;
	value: number;
	onSelectChecked?: (selected: boolean) => void;
	onSliderValueChanged?: (value: number) => void;
	invalid: boolean;
	redesigned: boolean;
	tone: 'primary' | 'neutral';
	accent?: string;
}

// The checkbox (highest-score categories) or weight slider — the right-hand
// control in both the classic row and the redesigned card.
const ComponentControl = ({
	checkBox,
	disableSlider,
	enabledChecked,
	value,
	onSelectChecked,
	onSliderValueChanged,
	invalid,
	redesigned,
	tone,
	accent,
}: ComponentControlProps) => {
	const { t } = useGlobalization();

	if (checkBox) {
		return (
			<div style={{ width: 'calc( 100% - 48px )' }}>
				<Checkbox
					disabled={disableSlider}
					checked={enabledChecked}
					onChange={(_: object, checked: boolean) =>
						onSelectChecked?.(checked)
					}
				/>
			</div>
		);
	}

	return (
		<div style={{ width: 'calc( 100% - 48px )' }}>
			{redesigned && (
				<Typography sx={{ fontSize: 12.5, color: '#5b6472', mb: 0.5 }}>
					{t('Component weight (% within category)')}
				</Typography>
			)}
			<PercentSlider
				value={enabledChecked ? value : 0}
				onSliderChanged={onSliderValueChanged}
				disabled={disableSlider}
				invalid={invalid}
				marks={redesigned ? 'off' : 'minmaxticks'}
				valueLabelDisplay={redesigned ? 'off' : 'on'}
				redesigned={redesigned}
				tone={tone}
				accent={accent}
				maxWidth={redesigned ? 480 : undefined}
			/>
		</div>
	);
};

interface ComponentCardViewProps {
	className: string;
	editable: boolean;
	onCardMouseDown: (e: React.MouseEvent) => void;
	onCardClick: () => void;
	dataCy: string;
	dataCategory?: string;
	controlAreaRef: React.RefObject<HTMLDivElement>;
	labelArea: React.ReactNode;
	controlContent: React.ReactNode;
}

// The redesigned (card) view: the whole tile is a mouse-only click target
// (see Component's own comment on why it isn't a semantic button), and the
// control column always uses the card's fixed proportions/padding — no
// ternary needed here since this view is only ever rendered when isCard.
const ComponentCardView = ({
	className,
	editable,
	onCardMouseDown,
	onCardClick,
	dataCy,
	dataCategory,
	controlAreaRef,
	labelArea,
	controlContent,
}: ComponentCardViewProps) => (
	<Box
		className={className}
		onMouseDown={editable ? onCardMouseDown : undefined}
		onClick={editable ? onCardClick : undefined}
		sx={editable ? { cursor: 'pointer' } : undefined}
		data-cy={dataCy}
		data-category={dataCategory}>
		<Grid container alignItems="center" spacing={2} wrap="nowrap">
			{labelArea}
			<Grid
				item
				xs={7}
				container
				justifyContent="space-between"
				ref={controlAreaRef}
				onClick={e => e.stopPropagation()}
				onKeyDown={e => e.stopPropagation()}
				style={{ paddingLeft: 0 }}>
				{controlContent}
			</Grid>
		</Grid>
	</Box>
);

interface ComponentRowViewProps {
	className: string;
	dataCy: string;
	dataCategory?: string;
	checkBox: boolean;
	labelArea: React.ReactNode;
	controlContent: React.ReactNode;
}

// The classic (flag-off) row view — untouched from before the redesign.
const ComponentRowView = ({
	className,
	dataCy,
	dataCategory,
	checkBox,
	labelArea,
	controlContent,
}: ComponentRowViewProps) => (
	<Grid
		container
		alignItems="center"
		className={className}
		data-cy={dataCy}
		data-category={dataCategory}>
		{labelArea}
		<Grid
			item
			xs={10}
			container
			justifyContent="space-between"
			style={{ paddingLeft: checkBox ? '18px' : '40px' }}>
			{controlContent}
		</Grid>
	</Grid>
);

export const Component = ({
	title,
	subTitle,
	value,
	disableSlider = false,
	invalid = false,
	onSliderValueChanged,
	onSelectChecked,
	needMenu = true,
	onEdit,
	onDelete,
	checkBox = false,
	enabledChecked,
	missing = false,
	missingKind,
	needsScoreCount = 0,
	categoryName,
	coverage,
	thinDataWarning = false,
	redesigned = false,
	tone = 'primary',
	accent,
}: ComponentProps) => {
	const classes = useStyles();
	const controlAreaRef = useRef<HTMLDivElement>(null);
	// Tracks whether the mousedown that started this click began inside the
	// control area — a slider/checkbox drag can end (mouseup) outside it,
	// e.g. over the label column, in which case the browser fires `click` on
	// their common ancestor (the card), bypassing controlArea's own
	// stopPropagation entirely. Checking where the press started, not just
	// where the click landed, catches that case too.
	const pressStartedInControlArea = useRef(false);

	const isCard = redesigned;
	const editable = redesigned && Boolean(onEdit);

	const handleCardMouseDown = (e: React.MouseEvent) => {
		pressStartedInControlArea.current = Boolean(
			controlAreaRef.current?.contains(e.target as Node),
		);
	};

	const handleCardClick = () => {
		if (pressStartedInControlArea.current) return;
		onEdit?.(title);
	};

	const labelArea = (
		<ComponentLabelArea
			isCard={isCard}
			title={title}
			subTitle={subTitle}
			missing={missing}
			missingKind={missingKind}
			needsScoreCount={needsScoreCount}
			coverage={coverage}
			thinDataWarning={thinDataWarning}
			editable={editable}
			onEdit={onEdit}
		/>
	);

	// Stops propagation so interacting with the checkbox, slider, input or
	// delete button doesn't bubble up to the card's own click-to-edit handler.
	// (See pressStartedInControlArea above for the drag-release case this
	// alone doesn't cover.)
	const controlContent = (
		<>
			<ComponentControl
				checkBox={checkBox}
				disableSlider={disableSlider}
				enabledChecked={enabledChecked}
				value={value}
				onSelectChecked={onSelectChecked}
				onSliderValueChanged={onSliderValueChanged}
				invalid={invalid}
				redesigned={redesigned}
				tone={tone}
				accent={accent}
			/>
			<ComponentActions
				needMenu={needMenu}
				redesigned={redesigned}
				title={title}
				onEdit={onEdit}
				onDelete={onDelete}
			/>
		</>
	);

	const dataCy = `component-${title}`;

	// Card layout: the border/padding/margin/hover live on a plain Box, not
	// the Grid container — Grid's own `spacing` applies a negative margin to
	// the container to offset its items' padding, which (if put on the same
	// element as the border) pulls the visible box outside its layout box
	// and overlaps neighbouring cards. The Grid for the two-column layout
	// lives inside the Box instead, same as the spike.
	// The card's onClick is a mouse-only convenience so the whole tile is
	// clickable, not just the title — but it deliberately has no
	// role/tabIndex/aria-label of its own. ComponentTitle is the real
	// keyboard-accessible control; giving the card its own "button" role too
	// would nest interactive elements (checkbox/slider/input/delete button)
	// inside a button, which breaks accessibility tooling and screen readers.
	if (isCard) {
		return (
			<ComponentCardView
				className={classes.card}
				editable={editable}
				onCardMouseDown={handleCardMouseDown}
				onCardClick={handleCardClick}
				dataCy={dataCy}
				dataCategory={categoryName}
				controlAreaRef={controlAreaRef}
				labelArea={labelArea}
				controlContent={controlContent}
			/>
		);
	}

	return (
		<ComponentRowView
			className={classes.root}
			dataCy={dataCy}
			dataCategory={categoryName}
			checkBox={checkBox}
			labelArea={labelArea}
			controlContent={controlContent}
		/>
	);
};
